import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useSession } from '../context/SessionContext';
import { useCart } from '../context/CartContext';
import { STATUS_META, displayStatusKey, payMethodLabel, payStatusLabel, isUnpaidOnline, razorpayPayId } from '../lib/format';
import { ErrorBanner, EmptyState, Spinner } from '../lib/ui/Feedback';
import Button from '../lib/ui/Button';
import PageHeader from '../lib/ui/PageHeader';
import RazorpayPayStep from '../components/checkout/RazorpayPayStep';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const STEPS = [
  { key: 'pending', en: 'Placed', te: 'చేయబడింది' },
  { key: 'confirmed', en: 'Confirmed', te: 'నిర్ధారణ' },
  { key: 'dispatched', en: 'On Way', te: 'బయలుదేరింది' },
  { key: 'delivered', en: 'Done', te: 'వచ్చింది' },
];

function StatusBar({ status, te }) {
  if (status === 'cancelled') {
    return <div className="text-center text-red-600 font-extrabold text-xs py-1.5 bg-red-50 rounded-lg my-2">✕ {te ? 'ఆర్డర్ రద్దు చేయబడింది' : 'Order Cancelled'}</div>;
  }
  const activeIdx = STEPS.findIndex((s) => s.key === status);
  return (
    <div className="flex items-center my-3 px-1">
      {STEPS.map((step, i) => {
        const done = i <= activeIdx;
        const current = i === activeIdx;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center gap-1" style={{ flex: i < STEPS.length - 1 ? '0 0 auto' : 1 }}>
              <div className={`rounded-full flex items-center justify-center font-black transition-all ${current ? 'h-7 w-7 text-[13px]' : 'h-5 w-5 text-[11px]'} ${done ? 'bg-brand text-white' : 'bg-black/10 text-ink-faint'}`} style={current ? { boxShadow: '0 0 0 3px rgba(45,106,39,0.2)' } : undefined}>
                {done ? '✓' : i + 1}
              </div>
              <div className={`text-[10px] font-bold whitespace-nowrap ${done ? 'text-brand' : 'text-ink-faint'}`}>{te ? step.te : step.en}</div>
            </div>
            {i < STEPS.length - 1 ? <div className={`flex-1 h-0.5 mx-1 mb-3.5 ${i < activeIdx ? 'bg-brand' : 'bg-black/10'}`} /> : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function OrderDetailPage() {
  const { id } = useParams();
  const { lang } = useLang();
  const te = lang === 'te';
  const { sessionUser } = useSession();
  const { addItem } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);
  const [error, setError] = useState('');
  const [cancelConf, setCancelConf] = useState(false);
  const [returnConf, setReturnConf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paySession, setPaySession] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const [cancelMsg, setCancelMsg] = useState('');

  const authHeaders = () => (sessionUser?.token ? { Authorization: `Bearer ${sessionUser.token}` } : {});

  const reload = async () => {
    if (!sessionUser?.phone) return;
    try {
      const res = await axios.get(`${API}/api/orders/track/${encodeURIComponent(sessionUser.phone)}`, { headers: authHeaders() });
      const found = (res.data.orders || []).find((o) => o.id === id);
      if (found) setOrder(found);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (order) { setLoading(false); return; }
    if (!sessionUser?.phone) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      await reload();
      setLoading(false);
    })();
  }, [id]);

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (!order) {
    return (
      <div className="max-w-2xl mx-auto">
        <PageHeader title={te ? 'ఆర్డర్' : 'Order'} fallback="/orders" />
        <EmptyState icon="📦" title={te ? 'ఆర్డర్ కనబడలేదు' : 'Order not found'} sub={te ? 'ఆర్డర్లకు తిరిగి వెళ్లి వెతకండి.' : 'Go back to Orders and search again.'} action={<Button onClick={() => navigate('/orders')}>{te ? 'ఆర్డర్లు' : 'Orders'}</Button>} />
      </div>
    );
  }

  const items = Array.isArray(order.items) ? order.items : [];
  const date = new Date(order.created_at);
  const deliveryCharge = parseFloat(order.delivery_charge) || 0;
  const itemsTotal = items.reduce((s, i) => s + parseFloat(i.subtotal || 0), 0);
  const meta = STATUS_META[displayStatusKey(order)] || STATUS_META.pending;
  const canCancel = ['pending', 'confirmed', 'dispatched'].includes(order.status);
  const canReturn = order.status === 'delivered';
  const isReturnRequested = order.status === 'return_requested';
  const isReturned = order.status === 'returned';

  const requireAuth = () => {
    if (sessionUser?.token) return true;
    setError(te ? 'లాగిన్ అవసరం' : 'Login required for this action');
    return false;
  };

  const cancelOrder = async () => {
    if (!requireAuth()) return;
    setSaving(true); setError('');
    try {
      await axios.patch(`${API}/api/orders/${order.id}/cancel`, {}, { headers: authHeaders() });
      setCancelConf(false);
      const isOnline = ['razorpay', 'cashfree', 'upi'].includes(String(order.payment_method || '').toLowerCase());
      setCancelMsg(isOnline
        ? (te ? 'ఆర్డర్ రద్దు అయింది. రీఫండ్ ప్రారంభించబడుతుంది.' : 'Order cancelled. Refund will be initiated.')
        : (te ? 'ఆర్డర్ రద్దు చేయబడింది.' : 'Order cancelled.'));
      await reload();
    } catch (e) {
      setError(e.response?.data?.error || (te ? 'రద్దు చేయడం విఫలమైంది' : 'Could not cancel order'));
    } finally { setSaving(false); }
  };

  const reorder = () => {
    const its = Array.isArray(order.items) ? order.items : [];
    let added = 0;
    its.forEach((it) => {
      const qty = Number(it.quantity_kg) || 0;
      if (!it.product_id || qty <= 0) return;
      addItem({
        id: it.product_id,
        name_en: it.product_name,
        name_te: it.product_name_te,
        sell_price: it.price_per_kg,
        price_per_kg: it.price_per_kg,
        unit: it.unit,
        image_url: it.image_url,
      }, qty);
      added += 1;
    });
    if (!added) {
      setError(te ? 'వస్తువులు కార్ట్‌కి జోడించలేకపోయాం' : 'Could not add these items to cart');
      return;
    }
    navigate('/cart');
  };

  const requestReturn = async () => {
    if (!requireAuth()) return;
    setSaving(true); setError('');
    try {
      await axios.patch(`${API}/api/orders/${order.id}/request-return`, {}, { headers: authHeaders() });
      setReturnConf(false);
      await reload();
    } catch (e) {
      setError(e.response?.data?.error || (te ? 'రిటర్న్ అభ్యర్థన విఫలమైంది' : 'Return request failed'));
    } finally { setSaving(false); }
  };

  const startPendingPay = async () => {
    setError(''); setPayBusy(true);
    try {
      const sess = await axios.post(`${API}/api/payments/razorpay/order`, { order_id: order.id }, { headers: authHeaders() });
      if (sess.data?.already_paid) { await reload(); return; }
      setPaySession({ ...order, ...sess.data, order_id: order.id, order_number: order.order_number, amount_due: Number(order.total_amount || sess.data.amount_rupees || 0), authToken: sessionUser?.token });
    } catch (e) {
      setError(e.response?.data?.error || e.message || (te ? 'చెల్లింపు ప్రారంభించలేకపోయాం' : 'Could not start payment'));
    } finally { setPayBusy(false); }
  };

  const switchToCod = async () => {
    setError(''); setPayBusy(true);
    try {
      await axios.patch(`${API}/api/orders/${encodeURIComponent(order.id)}/switch-cod`, {}, { headers: authHeaders() });
      await reload();
    } catch (e) {
      setError(e.response?.data?.error || e.message || (te ? 'COD కు మారలేకపోయాం' : 'Could not switch to COD'));
    } finally { setPayBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title={`#${order.order_number}`} sub={`${date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`} fallback="/orders" />

      <div className="px-4 md:px-6 py-4">
        <div className="flex justify-end mb-1">
          <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full" style={{ background: meta.bg, color: meta.color }}>{meta.icon} {te ? meta.labelTe : meta.label}</span>
        </div>

        <StatusBar status={order.status} te={te} />

        <div className="text-[11px] font-extrabold text-ink-faint uppercase tracking-wide mb-2 mt-4">{te ? 'వస్తువులు' : 'Order Items'}</div>
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden mb-4">
          {items.map((item, i) => (
            <div key={i} className={`flex items-center px-3.5 py-2.5 ${i < items.length - 1 ? 'border-b border-black/5' : ''}`}>
              <span className="flex-1 text-[13px] font-bold text-ink">{te ? (item.product_name_te || item.product_name) : item.product_name}</span>
              <span className="text-xs text-ink-faint ml-2 shrink-0">{item.quantity_kg}{item.unit || 'kg'}</span>
              <span className="font-extrabold text-brand ml-3 text-[13px] shrink-0">₹{parseFloat(item.subtotal).toFixed(0)}</span>
            </div>
          ))}
        </div>

        <div className="bg-brand-light/40 rounded-2xl px-3.5 py-3 mb-4">
          <div className="flex justify-between text-[13px] text-ink-soft mb-1.5">
            <span>{te ? 'వస్తువుల మొత్తం' : 'Items subtotal'}</span>
            <span className="font-bold">₹{itemsTotal.toFixed(2)}</span>
          </div>
          {deliveryCharge > 0 ? (
            <div className="flex justify-between text-[13px] text-gold mb-1.5">
              <span>⚡ {te ? 'వెంటనే డెలివరీ చార్జ్' : 'Immediate delivery charge'}</span>
              <span className="font-bold">+₹{deliveryCharge.toFixed(2)}</span>
            </div>
          ) : null}
          <div className="flex justify-between font-black text-[15px] text-brand-dark border-t border-brand-light pt-2 mt-1">
            <span>{isUnpaidOnline(order) ? (te ? 'చెల్లించాల్సినది' : 'Amount due') : (te ? 'మొత్తం' : 'Total')}</span>
            <span>₹{Number(order.total_amount).toFixed(2)}</span>
          </div>
        </div>

        {order.delivery_label ? (
          <div className="text-xs text-ink-soft bg-white border border-black/5 rounded-xl px-3.5 py-2.5 mb-4">📍 {order.delivery_label}</div>
        ) : null}

        <div className={`rounded-xl px-3.5 py-3 mb-4 ${isUnpaidOnline(order) ? 'bg-gold-light' : 'bg-black/[.03]'}`}>
          <div className="flex items-center gap-2.5">
            <span className="text-xl">{order.payment_method === 'upi' ? '📱' : (order.payment_method === 'razorpay' || order.payment_method === 'cashfree') ? '💳' : '💵'}</span>
            <div className="flex-1">
              <div className="text-[11px] text-ink-faint font-bold">{te ? 'చెల్లింపు పద్ధతి' : 'Payment method'}</div>
              <div className="text-[13px] font-extrabold text-ink">{payMethodLabel(order.payment_method)}</div>
            </div>
            <span className={`text-[11px] font-extrabold px-2.5 py-1 rounded-full ${isUnpaidOnline(order) ? 'bg-gold/20 text-gold' : 'bg-emerald-100 text-emerald-800'}`}>{payStatusLabel(order)}</span>
          </div>
          {razorpayPayId(order) ? <div className="text-xs text-ink-soft font-bold mt-2 break-all">Payment ID: <span className="font-mono">{razorpayPayId(order)}</span></div> : null}
        </div>

        <ErrorBanner>{error}</ErrorBanner>

        {cancelMsg ? (
          <div className="text-center text-sm font-bold text-ink py-2 px-3.5 bg-black/[.03] rounded-xl mb-4">{cancelMsg}</div>
        ) : null}

        {isUnpaidOnline(order) && sessionUser ? (
          <div className="flex gap-2.5 mb-4">
            <Button variant="blue" className="flex-1" loading={payBusy} onClick={startPendingPay}>{te ? 'ఇప్పుడు చెల్లించండి' : 'Pay now'}</Button>
            <Button variant="outline" className="flex-1 !border-gold !text-gold" loading={payBusy} onClick={switchToCod}>{te ? 'CODగా ఉంచు' : 'Place as COD'}</Button>
          </div>
        ) : null}

        <Button variant="outline" fullWidth className="mb-2.5" onClick={reorder}>🔁 {te ? 'మళ్ళీ ఆర్డర్ చేయండి' : 'Reorder'}</Button>

        {canCancel && !cancelConf ? (
          <Button variant="danger" fullWidth className="mb-2.5" onClick={() => setCancelConf(true)}>✕ {te ? 'ఆర్డర్ రద్దు చేయి' : 'Cancel Order'}</Button>
        ) : null}
        {cancelConf ? (
          <div className="bg-gold-light rounded-xl p-3.5 mb-2.5">
            <div className="font-bold text-sm text-gold mb-2">{te ? 'ఆర్డర్ రద్దు చేయాలా?' : 'Cancel this order?'}</div>
            <div className="flex gap-2">
              <Button variant="dangerSolid" className="flex-1" loading={saving} onClick={cancelOrder}>{te ? 'అవును, రద్దు చేయి' : 'Yes, Cancel'}</Button>
              <Button variant="ghost" onClick={() => setCancelConf(false)}>{te ? 'వద్దు' : 'Keep'}</Button>
            </div>
          </div>
        ) : null}

        {canReturn && !returnConf ? (
          <Button variant="outline" fullWidth className="mb-2.5" onClick={() => setReturnConf(true)}>↩ {te ? 'రిటర్న్ అభ్యర్థించు' : 'Request Return'}</Button>
        ) : null}
        {returnConf ? (
          <div className="bg-amber-50 rounded-xl p-3.5 mb-2.5">
            <div className="font-bold text-sm text-amber-800 mb-1">{te ? 'రిటర్న్ అభ్యర్థించాలా?' : 'Request a return for this order?'}</div>
            <div className="text-xs text-amber-700 mb-2.5">{te ? 'మా టీమ్ మీతో సంప్రదిస్తుంది.' : 'Our team will contact you to arrange the pickup.'}</div>
            <div className="flex gap-2">
              <Button className="flex-1 !bg-amber-800" loading={saving} onClick={requestReturn}>{te ? 'అవును, రిటర్న్' : 'Yes, Request Return'}</Button>
              <Button variant="ghost" onClick={() => setReturnConf(false)}>{te ? 'రద్దు' : 'Cancel'}</Button>
            </div>
          </div>
        ) : null}

        {isReturnRequested ? (
          <div className="bg-amber-50 rounded-2xl p-4 text-center">
            <div className="text-2xl mb-1">↩</div>
            <div className="font-extrabold text-amber-800">{te ? 'రిటర్న్ అభ్యర్థించారు' : 'Return Requested'}</div>
            <div className="text-xs text-amber-700 mt-1.5">{te ? 'మా టీమ్ మిమ్మల్ని సంప్రదిస్తుంది.' : 'Our team will contact you to arrange the pickup.'}</div>
          </div>
        ) : null}
        {isReturned ? (
          <div className="bg-black/5 rounded-2xl p-4 text-center">
            <div className="text-2xl mb-1">✔</div>
            <div className="font-extrabold text-ink-soft">{te ? 'ఆర్డర్ రిటర్న్ అయింది' : 'Order Returned'}</div>
          </div>
        ) : null}
      </div>

      {paySession ? (
        <div className="max-w-lg mx-auto px-4 md:px-6 pb-8">
          <RazorpayPayStep
            orderResult={paySession}
            te={te}
            L={(en, teVal) => (te ? teVal : en)}
            loading={payBusy}
            error={error}
            onPaid={async () => { setPaySession(null); await reload(); }}
            onBack={() => setPaySession(null)}
            onRetrySession={async () => {
              const sess = await axios.post(`${API}/api/payments/razorpay/order`, { order_id: order.id }, { headers: authHeaders() });
              const merged = { ...paySession, ...sess.data };
              setPaySession(merged);
              return merged;
            }}
            onSupportOpen={() => {}}
          />
        </div>
      ) : null}
    </div>
  );
}
